import moment from 'moment';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import * as graphql from '@treams/treams-network';
import { ActivityRow, CashIcon, FlexGap, Paragraph, tokens } from '@treams/treams-ui';

import { formatSalaryAtFte } from './formatSalary';

const JUST_NOW_SECONDS = 45;

const Name = styled.span`
	font-weight: ${tokens.typography.weights.medium};
`;

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const emphasizeNames = (sentence: string, names: string[]) => {
	const unique = [...new Set(names.filter(Boolean))].sort((a, b) => b.length - a.length);
	if (unique.length === 0) return sentence;

	const pattern = new RegExp(`(${unique.map(escapeRegExp).join('|')})`, 'g');
	return sentence
		.split(pattern)
		.map((part, index) => (unique.includes(part) ? <Name key={index}>{part}</Name> : part));
};

type Change = {
	id: string;
	amount: number;
	fteHours?: number | null;
	reason: graphql.SalaryChangeReason;
	bases: graphql.SalaryChangeBasis[];
	explanation?: string | null;
	createdAt: string;
	createdBy: { firstName: string; lastName: string };
};

export default function SalaryChangeRow(props: { change: Change }) {
	const { t, i18n } = useTranslation();
	const { change } = props;
	const actor = `${change.createdBy.firstName} ${change.createdBy.lastName}`;
	const amount = formatSalaryAtFte(change.amount, change.fteHours, i18n.language, (values) =>
		t('salaryDevelopment.fteHours.atFullTime', values),
	);
	const reason = t(`salaryDevelopment.reason.${change.reason}`);
	const bases = change.bases.map((basis) => t(`salaryDevelopment.basis.${basis}`)).join(', ');
	const timeAgo =
		Math.abs(moment().diff(change.createdAt, 'seconds')) < JUST_NOW_SECONDS
			? t('salaryDevelopment.activity.justNow')
			: moment(change.createdAt).fromNow();

	const details: ReactNode = (
		<ActivityRow.Details label={t('salaryDevelopment.activity.showBasis')}>
			<FlexGap flexDirection="column" gap={tokens.gap.inline}>
				<Paragraph small>{t('salaryDevelopment.activity.basis', { bases })}</Paragraph>
				{change.explanation && <ActivityRow.Quote>{change.explanation}</ActivityRow.Quote>}
			</FlexGap>
		</ActivityRow.Details>
	);

	return (
		<ActivityRow
			marker={
				<ActivityRow.Marker color={tokens.success.icon}>
					<CashIcon />
				</ActivityRow.Marker>
			}
			meta={timeAgo}
			metaTooltip={moment(change.createdAt).format('LLL')}
			details={details}
		>
			{emphasizeNames(t('salaryDevelopment.activity.adjusted', { actor, amount, reason }), [actor])}
		</ActivityRow>
	);
}
